import { API_BASE_URL } from "@kupikupi/brand";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addAddress,
  deleteAddressesBatch,
  editAddress,
  listAddresses,
  setDefaultAddress,
} from "../endpoints/address";
import type { AddressAddInput, AddressEditInput, AddressVo } from "../types/address";

const ADDRESS_LIST_KEY = ["addresses", "list"] as const;

export function useAddresses(authToken: string | null) {
  return useQuery<AddressVo[]>({
    queryKey: ADDRESS_LIST_KEY,
    enabled: !!authToken,
    queryFn: () => {
      if (!authToken) throw new Error("NO_TOKEN");
      return listAddresses({ baseUrl: API_BASE_URL }, authToken);
    },
    staleTime: 60_000,
  });
}

export function useAddAddress(authToken: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddressAddInput) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return addAddress({ baseUrl: API_BASE_URL }, authToken, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADDRESS_LIST_KEY });
    },
  });
}

export function useEditAddress(authToken: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddressEditInput) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return editAddress({ baseUrl: API_BASE_URL }, authToken, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADDRESS_LIST_KEY });
    },
  });
}

export function useSetDefaultAddress(authToken: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return setDefaultAddress({ baseUrl: API_BASE_URL }, authToken, id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADDRESS_LIST_KEY });
    },
  });
}

export function useDeleteAddresses(authToken: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return deleteAddressesBatch({ baseUrl: API_BASE_URL }, authToken, ids);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADDRESS_LIST_KEY });
    },
  });
}
